import { useCallback, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { LIS } from "shared/actions/constants";
import * as frontLoaderActions from "shared/actions/frontLoaderActions";
import { GateProps } from "shared/components/GateProps";
import { useHasLoaded } from "shared/hooks/requestStatusHooks";
import useWebSocket from "shared/hooks/useWebSocket";

import { useAppDispatch } from "app/hooks";
import SelectedStationContext from "app/SelectedStationContext";
import { printCassetteBlockNames } from "hooks/useBarcodePrintingCallbacks";

const LabFrontLoadGate = ({ children, LoadingEl, ErrorEl }: GateProps) => {
  const dispatch = useAppDispatch();
  const selectedStation = useContext(SelectedStationContext);

  const onMessageData = useCallback((data: any) => {
    if (
      data.type === "cassette.printer" &&
      ["succeeded", "failed"].includes(data.status)
    ) {
      dispatch((d, getState) => {
        const state = getState();
        if (!state.sys.taskIds.includes(data.task_id)) return;

        const blockNames = printCassetteBlockNames(state, data.block_ids);
        if (data.status === "succeeded") {
          d({
            type: LIS.BLOCK.PRINT_CASSETTES.SUCCESS,
            payload: data,
          });
        } else if (data.status === "failed") {
          d({
            type: LIS.BLOCK.PRINT_CASSETTES.ERROR,
            payload: data,
          });
          toast.error(
            `Failed to print Cassette${
              data.block_ids.length === 1 ? "" : "s"
            } ${blockNames}`
          );
        }
      });
    }
  }, []);

  useWebSocket(`lab-${selectedStation.labId}`, true, {
    onMessageData,
  });

  const [hasErrors, setHasErrors] = useState(false);
  const hasLoadedAccessionPrefixes = useHasLoaded(
    frontLoaderActions.frontLoadAccessionPrefixes,
    { lab_id: selectedStation.labId || 0 }
  );
  useEffect(() => {
    dispatch(
      frontLoaderActions.frontLoadAccessionPrefixes({
        lab_id: selectedStation.labId || 0,
      })
    ).catch(() => setHasErrors(true));
  }, [selectedStation.labId]);

  const hasLoadedLabClinics = useHasLoaded(
    frontLoaderActions.frontloadLabClinics,
    { labstation_id: selectedStation.stationId || 0 }
  );
  useEffect(() => {
    dispatch(
      frontLoaderActions.frontloadLabClinics({
        labstation_id: selectedStation.stationId || 0,
      })
    ).catch(() => setHasErrors(true));
  }, [selectedStation.stationId]);

  if (hasErrors)
    return (
      <ErrorEl className="LabFrontLoadGate">Failed to load Lab Data</ErrorEl>
    );

  if (!hasLoadedAccessionPrefixes || !hasLoadedLabClinics) {
    return (
      <LoadingEl className="LabFrontLoadGate">Loading Lab Data...</LoadingEl>
    );
  }

  return <>{children}</>;
};

export default LabFrontLoadGate;
